import athenaConfig from '../config';
import AmplitudeUtils from './amplitude-utils';
import { PreAuthnError } from './errors';
import { getOIEStateHandle } from './widget-facade';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import asyncRetry from 'async-retry';

interface ResponseWithBody extends Response {
  responseBody?: any;
}

const iamCoreUrl = athenaConfig.iamCoreUrl;
function isRetryableError(response: ResponseWithBody) {
  return !response || (response.status >= 500 && response.status <= 599);
}

interface Options {
  method: string;
  headers: Record<string, string>;
  body?: string;
}

interface MakeIamCoreRequestProps extends asyncRetry.Options {
  iamCoreApiEndpoint: string;
  options: Options;
}

function generateOptions(data: Record<any, any>): Options {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(data),
  };
}

export const makeIamCoreRequest = async ({
  iamCoreApiEndpoint,
  options,
  onRetry = () => ({}),
}: MakeIamCoreRequestProps) => {
  return asyncRetry(
    async (bail) => {
      const abortController = new AbortController();
      const id = setTimeout(() => abortController.abort(), 30000);
      const response = await fetch(iamCoreApiEndpoint, {
        ...options,
        signal: abortController.signal,
      });
      const result = await response.json();
      if (response.ok) {
        return result;
      }
      const error: ResponseWithBody = response;
      error.responseBody = result;
      clearTimeout(id);
      if (isRetryableError(error)) {
        throw error;
      }
      bail(error);
    },
    { retries: 2, factor: 1, minTimeout: 2000, onRetry }
  );
};

export async function resetUserAccount() {
  const stateHandle = getOIEStateHandle();
  const requestData = {
    stateHandle,
    retryAttempts: 2,
    retryTimeout: 5000,
    clientId: settings.clientId,
  };
  return await makeIamCoreRequest({
    iamCoreApiEndpoint: `${iamCoreUrl}api/v1/users/resetSelf`,
    options: generateOptions(requestData),
  });
}

export interface ProcessCredsPayload {
  username: string;
  updateUserMfaProfile: boolean;
}

export async function processCredsApi(payload: ProcessCredsPayload) {
  try {
    await makeIamCoreRequest({
      iamCoreApiEndpoint: `${iamCoreUrl}api/v1/process-creds`,
      options: generateOptions(payload),
    });
  }
  catch (error) {
    AmplitudeUtils.logCustomEvent('ProcessCredsError', { error });
  }
}

export interface PreAuthnRequestDetails {
  clientId?: string | null;
  scope?: string | null;
  redirectUri?: string | null;
  aud?: string | null;
  emailToken?: string | null;
  idp?: string | null;
  fhir?: boolean;
  authServerId: string;
  lwaUniqId?: string;
  uiLocales?: string | null;
  lwaClientInteractionCodeFlowProps?: {
    clientId: string;
    redirectUri: string;
    scope: string;
    codeChallenge: string;
    state: string;
  };
}

interface PreAuthnRequest extends PreAuthnRequestDetails {
  source: 'ohsw';
}

export interface PreAuthnConfigsResponse {
  displayName?: string;
  username?: string;
  language?: string;
  userNamespace?: string;
  showSetPasswordDescription?: boolean;
  stateHandle?: string;
  emailTokenUserRegisteredWarning?: boolean;
  tempPasswordHint?: boolean;
  callProcessCreds?: boolean;
  updateUserMfaProfile?: boolean;
  allowRegistration?: boolean;
  signupLink?: string;
  showTermsText?: boolean;
  newUser?: boolean;
  originalRedirectUri?: URL;
  hideResetAccountOption?: boolean;
  idp?: string;
  workflowType?: string;
}

export async function fetchPreAuthnConfigs(preAuthnInfo: PreAuthnRequestDetails): Promise<PreAuthnConfigsResponse> {
  try {
    const filteredPayload: PreAuthnRequest = { ...preAuthnInfo, source: 'ohsw' };
    Object.keys(filteredPayload).forEach((acc) => {
      const key = acc as keyof PreAuthnRequestDetails;
      if (filteredPayload[key] === null) {
        delete filteredPayload[key];
      }
    });

    const preAuthnConfigs = await makeIamCoreRequest({
      iamCoreApiEndpoint: `${iamCoreUrl}api/v1/lwa-config/pre-authn`,
      options: generateOptions({ preAuthnInfo: filteredPayload }),
    });
    preAuthnConfigs.stateHandle = preAuthnConfigs.stateToken;
    return preAuthnConfigs;
  }
  catch (error) {
    AmplitudeUtils.logCustomEvent('PreAuthnConfigsError', { error });
    throw new PreAuthnError(error);
  }
}
